import { memo, ReactElement, useState, useEffect, useRef } from 'react';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { MAX_PLAYERS_PER_EVENT } from '../../constants/limits';
import Event from '../../types/Event';

import { FieldTarget } from './eventPreviewTypes';

type EventTypeEditorProps = {
  event: Event;
  isGolf: boolean;
  onGolfChange: (isGolf: boolean) => void;
  onUpdate: (updates: Partial<Event>) => void;
  onParticipantOrderChange?: (order: 'time' | 'index') => void;
  /** Field to focus when the editor expands */
  focusTarget?: FieldTarget | null;
  /** Called after focus has been applied */
  onFocusComplete?: () => void;
};

const EventTypeEditor = memo(function EventTypeEditor(props: EventTypeEditorProps): ReactElement {
  const { event, isGolf, onGolfChange, onUpdate, focusTarget, onFocusComplete } = props;
  const safeMaxPlayers = typeof event.maxPlayers === 'number' && !isNaN(event.maxPlayers) ? event.maxPlayers : 0;
  const hasNoLimit = safeMaxPlayers === -1;
  const maxPlayersRef = useRef<HTMLInputElement>(null);

  // Local state for the input field to allow temporary empty values while typing
  const [inputValue, setInputValue] = useState(safeMaxPlayers > 0 ? String(safeMaxPlayers) : '');

  // Handle focus target from parent
  useEffect(() => {
    if (focusTarget === 'maxPlayers' && maxPlayersRef.current) {
      maxPlayersRef.current.focus();
      maxPlayersRef.current.select();
      onFocusComplete?.();
    }
  }, [focusTarget, onFocusComplete]);

  // Sync local state when event.maxPlayers changes externally
  useEffect(() => {
    setInputValue(safeMaxPlayers > 0 ? String(safeMaxPlayers) : '');
  }, [safeMaxPlayers]);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        What type of event and how many attendees can sign up?
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{
        alignItems: { xs: 'flex-start', sm: 'center' }
      }}>
        <ToggleButtonGroup
          color="primary"
          value={isGolf ? 'golf' : 'social'}
          exclusive
          onChange={(_, value) => {
            if (value) {
              onGolfChange(value === 'golf');
              onUpdate({
                isGolf: value === 'golf',
              });
            }
          }}
          size="small"
          aria-label="Event Type"
        >
          <ToggleButton value="golf">Golf</ToggleButton>
          <ToggleButton value="social">Social</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={2} sx={{
          alignItems: "center"
        }}>
          {!hasNoLimit && (
            <TextField
              id="maxPlayers"
              label={isGolf ? "Max golfers" : "Max attendees"}
              variant="outlined"
              size="small"
              inputRef={maxPlayersRef}
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  type: 'number',
                  min: 1,
                  max: MAX_PLAYERS_PER_EVENT,
                },
              }}
              sx={{ width: 120 }}
              value={inputValue}
              onChange={({ target }) => {
                const value = target.value;
                setInputValue(value); // Always update local state to allow typing

                if (value === '') {
                  return; // Don't update parent if empty (user is still typing)
                }
                const numValue = parseInt(value, 10);
                if (!isNaN(numValue) && numValue >= 1 && numValue <= MAX_PLAYERS_PER_EVENT) {
                  onUpdate({ maxPlayers: numValue });
                }
              }}
              onBlur={() => {
                const numValue = parseInt(inputValue, 10);
                if (inputValue === '' || isNaN(numValue) || numValue < 1) {
                  setInputValue(safeMaxPlayers > 0 ? String(safeMaxPlayers) : '20');
                  if (safeMaxPlayers <= 0) {
                    onUpdate({ maxPlayers: 20 });
                  }
                }
              }}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={hasNoLimit}
                onChange={(e) => {
                  onUpdate({
                    maxPlayers: e.target.checked ? -1 : 20,
                  });
                }}
              />
            }
            label="No attendee max"
            sx={{ whiteSpace: 'nowrap' }}
          />
          {isGolf && (
            <TextField
              select
              label="Tee sheet group size"
              size="small"
              sx={{ minWidth: '8em' }}
              value={event.teeSheetGroupSize ?? 4}
              onChange={(e) => onUpdate({ teeSheetGroupSize: Number(e.target.value) })}
            >
              {[3, 4].map((size) => (
                <MenuItem key={size} value={size}>{size} per group</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});

export default EventTypeEditor;
